import { supabase } from './supabase';

// ============================================================
// TYPES ÉTENDUS POUR JOURNALISATION COMPLÈTE
// ============================================================

export type ActionType =
  // Accès aux données
  | 'VIEW' | 'CREATE' | 'UPDATE' | 'DELETE' | 'DOWNLOAD' | 'SHARE'
  // Gestion des utilisateurs
  | 'PROMOTE' | 'REVOKE' | 'INVITE' | 'REGISTER' | 'SUSPEND' | 'REACTIVATE'
  // Authentification
  | 'LOGIN' | 'LOGOUT' | 'LOGIN_FAILED' | 'PASSWORD_CHANGE' | 'PASSWORD_RESET'
  // Configuration système
  | 'CONFIG_UPDATE' | 'MAINTENANCE_TOGGLE'
  // Communications
  | 'SEND_SMS' | 'SEND_EMAIL' | 'SEND_NOTIFICATION';

export type ResourceType =
  // Ressources médicales
  | 'DOSSIER_MEDICAL' | 'PRESCRIPTION' | 'RESULTAT_TEST' | 'RENDEZ_VOUS' | 'SUIVI'
  // Gestion du personnel
  | 'ADMINISTRATEUR' | 'MEDECIN' | 'SECRETAIRE' | 'PATIENT'
  // Système
  | 'SYSTEM_CONFIG' | 'NOTIFICATION' | 'ACTUALITE' | 'SESSION'
  // Communications
  | 'MESSAGE_CONTACT' | 'SMS' | 'EMAIL'
  // Autres
  | 'CONSULTATION' | 'PARTAGE_DOSSIER' | 'DISPONIBILITE'
  // Laboratoire
  | 'ANALYSE_CATALOGUE' | 'BON_LABORATOIRE' | 'RESULTAT_ANALYSE';

export interface LogAccessParams {
  userId: string | null;
  userRole: string;
  actionType: ActionType;
  resourceType: ResourceType;
  resourceId?: string;
  patientId?: string;
  details?: Record<string, unknown>;
}

export interface LogAccessResult {
  success: boolean;
  logId?: string;
  error?: string;
}

// ============================================================
// FONCTION DE LOGGING CÔTÉ CLIENT (fallback)
// ============================================================

export async function logAccess(params: LogAccessParams): Promise<void> {
  try {
    let ipAddress: string | null = null;
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      ipAddress = ipData.ip;
    } catch {
      // Silencieux
    }

    const userAgent = navigator.userAgent;

    const { error } = await supabase.from('access_logs').insert({
      user_id: params.userId,
      user_role: params.userRole,
      action_type: params.actionType,
      resource_type: params.resourceType,
      resource_id: params.resourceId || null,
      patient_id: params.patientId || null,
      ip_address: ipAddress,
      user_agent: userAgent,
      details: params.details || null,
    });

    if (error) {
      // Silencieux
    }
  } catch (error) {
    // Silencieux
  }
}

// ============================================================
// FONCTION DE LOGGING CÔTÉ SERVEUR (recommandée - IP fiable)
// ============================================================

export async function logAccessServer(params: LogAccessParams): Promise<LogAccessResult> {
  try {
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    if (!token) {
      return { success: false, error: 'Session expirée' };
    }

    const response = await supabase.functions.invoke('log-access', {
      body: {
        actionType: params.actionType,
        resourceType: params.resourceType,
        resourceId: params.resourceId,
        patientId: params.patientId,
        details: params.details,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.error) {
      await logAccess(params);
      return { success: false, error: 'Fallback au logging client' };
    }

    return { success: true, logId: response.data?.logId };
  } catch (error: any) {
    await logAccess(params);
    return { success: false, error: error?.message || 'Erreur inconnue' };
  }
}

// ============================================================
// LOGGING OFFLINE — QUEUE POUR CONFORMITÉ
// ============================================================
// Les logs d'accès créés hors-ligne sont mis en file d'attente
// avec le timestamp RÉEL de l'action (et non l'heure du replay).
// Ils sont synchronisés automatiquement au retour réseau.
// ============================================================

const OFFLINE_LOG_QUEUE_KEY = 'csf-offline-logs';

interface OfflineLogEntry {
  id: string;
  params: LogAccessParams;
  /** Timestamp réel de l'action (et non du replay) */
  actionTimestamp: number;
  createdAt: number;
}

function getOfflineLogQueue(): OfflineLogEntry[] {
  try {
    const raw = localStorage.getItem(OFFLINE_LOG_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveOfflineLogQueue(queue: OfflineLogEntry[]): void {
  try {
    localStorage.setItem(OFFLINE_LOG_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    console.warn('[AccessLogs] Impossible de sauvegarder les logs offline');
  }
}

/**
 * Log un accès — version offline-aware.
 * Si online : log directement (client + serveur).
 * Si offline : met en file d'attente avec le timestamp réel.
 */
export async function logAccessOfflineAware(params: LogAccessParams): Promise<void> {
  if (navigator.onLine) {
    // Online → log immédiat
    await logAccessServer(params).catch(() => {
      // Fallback : queue quand même si le serveur est injoignable
      queueOfflineLog(params);
    });
  } else {
    // Offline → queue avec timestamp réel
    queueOfflineLog(params);
  }
}

function queueOfflineLog(params: LogAccessParams): void {
  const queue = getOfflineLogQueue();
  queue.push({
    id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    params: { ...params },
    actionTimestamp: Date.now(),
    createdAt: Date.now(),
  });
  saveOfflineLogQueue(queue);
}

/**
 * Rejoue tous les logs d'accès en attente.
 * Appelé automatiquement au retour en ligne.
 */
export async function replayOfflineLogs(): Promise<{ sent: number; failed: number; remaining: number }> {
  const queue = getOfflineLogQueue();
  if (queue.length === 0) return { sent: 0, failed: 0, remaining: 0 };

  let sent = 0;
  let failed = 0;
  const failedLogs: OfflineLogEntry[] = [];

  // Trier par ordre chronologique (les plus anciens d'abord)
  const sorted = [...queue].sort((a, b) => a.actionTimestamp - b.actionTimestamp);

  for (const entry of sorted) {
    try {
      // Ajouter le timestamp réel dans les détails
      const paramsWithTimestamp: LogAccessParams = {
        ...entry.params,
        details: {
          ...(entry.params.details || {}),
          _offlineActionTimestamp: new Date(entry.actionTimestamp).toISOString(),
          _offlineLoggedAt: new Date(entry.createdAt).toISOString(),
        },
      };

      const result = await logAccessServer(paramsWithTimestamp);
      if (result.success) {
        sent++;
      } else {
        failed++;
        failedLogs.push(entry);
      }
    } catch {
      failed++;
      failedLogs.push(entry);
    }
  }

  saveOfflineLogQueue(failedLogs);
  return { sent, failed, remaining: failedLogs.length };
}

/**
 * Vide la file d'attente des logs offline.
 */
export function clearOfflineLogs(): void {
  saveOfflineLogQueue([]);
}

/**
 * Nombre de logs en attente.
 */
export function getOfflineLogCount(): number {
  return getOfflineLogQueue().length;
}

// ============================================================
// HELPERS PRATIQUES POUR CHAQUE TYPE D'OPÉRATION
// ============================================================

export async function logPatientCreation(userId: string, patientId: string, patientName: string, createdBy: string) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'CREATE',
    resourceType: 'PATIENT',
    resourceId: patientId,
    details: {
      patientName,
      createdBy,
      operation: 'Création de patient',
    },
  });
}

export async function logMedecinCreation(userId: string, medecinId: string, medecinName: string, email: string) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'INVITE',
    resourceType: 'MEDECIN',
    resourceId: medecinId,
    details: {
      medecinName,
      email,
      operation: 'Invitation médecin',
    },
  });
}

export async function logSecretaireCreation(userId: string, secretaireId: string, secretaireName: string, email: string) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'CREATE',
    resourceType: 'SECRETAIRE',
    resourceId: secretaireId,
    details: {
      secretaireName,
      email,
      operation: 'Création secrétaire',
    },
  });
}

export async function logAdminPromotion(userId: string, targetUserId: string, targetName: string, targetEmail: string) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'PROMOTE',
    resourceType: 'ADMINISTRATEUR',
    resourceId: targetUserId,
    details: {
      targetName,
      targetEmail,
      operation: 'Promotion administrateur',
    },
  });
}

export async function logAdminRevocation(userId: string, targetUserId: string, targetName: string) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'REVOKE',
    resourceType: 'ADMINISTRATEUR',
    resourceId: targetUserId,
    details: {
      targetName,
      operation: 'Révocation administrateur',
    },
  });
}

export async function logAdminCreation(userId: string, targetUserId: string, targetName: string, targetEmail: string) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'CREATE',
    resourceType: 'ADMINISTRATEUR',
    resourceId: targetUserId,
    details: {
      targetName,
      targetEmail,
      operation: 'Création compte administrateur',
    },
  });
}

export async function logAdminDeletion(userId: string, targetUserId: string, targetName: string) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'DELETE',
    resourceType: 'ADMINISTRATEUR',
    resourceId: targetUserId,
    details: {
      targetName,
      operation: 'Suppression administrateur',
    },
  });
}

export async function logRendezVousCreation(userId: string, userRole: string, rendezVousId: string, patientName: string, date: string) {
  return logAccessServer({
    userId,
    userRole,
    actionType: 'CREATE',
    resourceType: 'RENDEZ_VOUS',
    resourceId: rendezVousId,
    details: {
      patientName,
      date,
      operation: 'Création rendez-vous',
    },
  });
}

export async function logPrescriptionCreation(userId: string, prescriptionId: string, patientName: string) {
  return logAccessServer({
    userId,
    userRole: 'medecin',
    actionType: 'CREATE',
    resourceType: 'PRESCRIPTION',
    resourceId: prescriptionId,
    details: {
      patientName,
      operation: 'Création prescription',
    },
  });
}

export async function logDossierAccess(userId: string, userRole: string, patientId: string, patientName: string) {
  return logAccessServer({
    userId,
    userRole,
    actionType: 'VIEW',
    resourceType: 'DOSSIER_MEDICAL',
    patientId,
    details: {
      patientName,
      operation: 'Consultation dossier médical',
    },
  });
}

export async function logSystemConfigUpdate(userId: string, configKey: string, oldValue: unknown, newValue: unknown) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'CONFIG_UPDATE',
    resourceType: 'SYSTEM_CONFIG',
    details: {
      configKey,
      oldValue,
      newValue,
      operation: 'Modification configuration système',
    },
  });
}

export async function logLoginSuccess(userId: string, userRole: string) {
  return logAccessServer({
    userId,
    userRole,
    actionType: 'LOGIN',
    resourceType: 'SESSION',
    details: {
      operation: 'Connexion réussie',
    },
  });
}

export async function logLoginFailed(email: string, reason: string) {
  // Pas de userId car la connexion a échoué
  try {
    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    if (!token) {
      // Même sans token, on essaie avec le client
      await logAccess({
        userId: null,
        userRole: 'unknown',
        actionType: 'LOGIN_FAILED',
        resourceType: 'SESSION',
        details: {
          email,
          reason,
          operation: 'Tentative de connexion échouée',
        },
      });
      return { success: true };
    }

    const response = await supabase.functions.invoke('log-access', {
      body: {
        actionType: 'LOGIN_FAILED',
        resourceType: 'SESSION',
        userId: null,
        userRole: 'unknown',
        details: {
          email,
          reason,
          operation: 'Tentative de connexion échouée',
        },
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return { success: !response.error };
  } catch {
    return { success: false };
  }
}

export async function logLogout(userId: string, userRole: string) {
  return logAccessServer({
    userId,
    userRole,
    actionType: 'LOGOUT',
    resourceType: 'SESSION',
    details: {
      operation: 'Déconnexion',
    },
  });
}

export async function logPasswordChange(userId: string, userRole: string, forced: boolean) {
  return logAccessServer({
    userId,
    userRole,
    actionType: 'PASSWORD_CHANGE',
    resourceType: 'SESSION',
    details: {
      forced,
      operation: forced ? 'Changement de mot de passe forcé' : 'Changement de mot de passe volontaire',
    },
  });
}

export async function logMaintenanceToggle(userId: string, enabled: boolean) {
  return logAccessServer({
    userId,
    userRole: 'super_admin',
    actionType: 'MAINTENANCE_TOGGLE',
    resourceType: 'SYSTEM_CONFIG',
    details: {
      enabled,
      operation: enabled ? 'Activation mode maintenance' : 'Désactivation mode maintenance',
    },
  });
}